import { useTranslations } from 'next-intl';
import {
  AnalyzeIconMobile,
  ArrowIcon,
  BubbleIcon2,
  CreateIconMobile,
  InteractIconMobile,
  RankingSimpleIcon,
  WCQSimpleIcon,
  ControlIcon,
  KeyIcon,
  LockIcon,
  ProfileIcon,
} from './Icons';
import type { CompanyRoleGroup } from './executiveRoles';

export const useHomepageData = (
  roleGroup: CompanyRoleGroup,
  isAuthenticated: boolean,
) => {
  const t = useTranslations('first_impression.legacy.common');

  const cta =
    roleGroup === 'executives'
      ? t(`home_page.hero.executives.cta`)
      : t(
          `home_page.hero.${roleGroup}.${
            isAuthenticated && roleGroup === 'default'
              ? 'cta_authenticated'
              : 'cta'
          }`,
        );

  const heroData = {
    title: t(`home_page.hero.${roleGroup}.title`),
    subtitle: t(`home_page.hero.${roleGroup}.subtitle`),
    cta,
    cta_description: !isAuthenticated
      ? t(`home_page.hero.default.cta_description`) // executives can only be identified as such when authenticated
      : '',
    roleGroup,
  };

  const desktopImageBlockData = {
    tag: t('home_page.desktop_image_block.tag'),
    title: t('home_page.desktop_image_block.title'),
    subtitle: t('home_page.desktop_image_block.subtitle'),
    slides: [
      {
        text: t('home_page.desktop_image_block.button_1'),
        icon: <WCQSimpleIcon />,
        desktop: {
          src: 'https://static.mentimeter.com/static/images/BreakTheIceBackgroundRebrand.png',
          alt: t('home_page.desktop_image_block.button_1'),
        },
        mobile: {
          src: 'https://static.mentimeter.com/static/images/BreakTheIcePhoneRebrand.png',
          alt: t('home_page.desktop_image_block.button_1'),
        },
      },

      {
        text: t('home_page.desktop_image_block.button_2'),
        icon: <BubbleIcon2 />,
        desktop: {
          src: 'https://static.mentimeter.com/static/images/GetInstantFeedbackBackgroundRebrand.png',
          alt: t('home_page.desktop_image_block.button_2'),
        },
        mobile: {
          src: 'https://static.mentimeter.com/static/images/GetInstantFeedbackPhoneRebrand.png',
          alt: t('home_page.desktop_image_block.button_2'),
        },
      },
      {
        text: t('home_page.desktop_image_block.button_3'),
        icon: <ArrowIcon />,
        desktop: {
          src: 'https://static.mentimeter.com/static/images/EvaluateFeedbackBackgroundRebrand2.png',
          alt: t('home_page.desktop_image_block.button_3'),
        },
        mobile: {
          src: 'https://static.mentimeter.com/static/images/EvaluateFeedbackPhoneRebrand.png',
          alt: t('home_page.desktop_image_block.button_3'),
        },
      },
      {
        text: t('home_page.desktop_image_block.button_4'),
        icon: <RankingSimpleIcon />,
        desktop: {
          src: 'https://static.mentimeter.com/static/images/MakeDecisionsBackgroundRebrand.png',
          alt: t('home_page.desktop_image_block.button_4'),
        },
        mobile: {
          src: 'https://static.mentimeter.com/static/images/MakeDecisionsPhoneRebrand.png',
          alt: t('home_page.desktop_image_block.button_4'),
        },
      },
    ],
  };
  const cardsData = {
    tag: t('home_page.cards_block.tag'),
    title: t('home_page.cards_block.title'),
    subtitle: t('home_page.cards_block.subtitle'),
    cards: [
      {
        text: {
          title: t('home_page.cards_block.card_1.title'),
          subtitle: t('home_page.cards_block.card_1.subtitle'),
          mobileIcon: <CreateIconMobile />,
        },
        image: {
          src: 'https://static.mentimeter.com/static/images/HowItWorksCreate.png',
          alt: t('home_page.cards_block.card_1.alt'),
        },
      },
      {
        text: {
          title: t('home_page.cards_block.card_2.title'),
          subtitle: t('home_page.cards_block.card_2.subtitle'),
          mobileIcon: <AnalyzeIconMobile />,
        },
        image: {
          src: 'https://static.mentimeter.com/static/images/HowItWorksInteract.png',
          alt: t('home_page.cards_block.card_2.alt'),
        },
      },
      {
        text: {
          title: t('home_page.cards_block.card_3.title'),
          subtitle: t('home_page.cards_block.card_3.subtitle'),
          mobileIcon: <InteractIconMobile />,
        },
        image: {
          src: 'https://static.mentimeter.com/static/images/HowItWorksAnalyze.png',
          alt: t('home_page.cards_block.card_3.alt'),
        },
      },
    ],
  };
  const halfColorData = [
    {
      image: {
        src: 'https://static.mentimeter.com/static/images/ClassroomBackground.webp',
        alt: t('home_page.half_color_block_1.alt'),
      },
      reverse: false,
      info: {
        tag: t('home_page.half_color_block_1.tag'),
        title: t('home_page.half_color_block_1.text'),
        author: {
          name: 'Teresa Delfin',
          role: 'California State Polytechnic University',
        },
        cta: {
          text: t('home_page.half_color_block_1.button'),
          url: '/education',
        },
      },
    },
    {
      image: {
        src: 'https://static.mentimeter.com/static/images/OfficeBackground.png',
        alt: t('home_page.half_color_block_2.alt'),
      },
      reverse: true,
      info: {
        tag: t('home_page.half_color_block_2.tag'),
        title: t('home_page.half_color_block_2.text'),
        author: {
          name: 'Merv Wyeth',
          role: 'Program Director at Project Management Institute',
        },
        cta: {
          text: t('home_page.half_color_block_2.button'),
          url: '/work',
        },
      },
    },
  ];
  const fullColorBlockData = {
    strong: t('home_page.full_color_block.strong'),
    title: t('home_page.full_color_block.title'),
    subtitle: t('home_page.full_color_block.subtitle'),
    cta: t('home_page.full_color_block.button'),
    bullets: [
      { text: t('home_page.full_color_block.bullet_1'), icon: <LockIcon /> },
      { text: t('home_page.full_color_block.bullet_2'), icon: <KeyIcon /> },
      { text: t('home_page.full_color_block.bullet_3'), icon: <ProfileIcon /> },
      { text: t('home_page.full_color_block.bullet_4'), icon: <ControlIcon /> },
    ],
  };
  return {
    heroData,
    fullColorBlockData,
    halfColorData,
    cardsData,
    desktopImageBlockData,
  };
};
